import { useEffect, useState } from "react";
import { ClearRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { PoGinesysSyncBadge, PoStatusBadge, poColors } from "../CreatePurchaseOrder/poUi";
import { listPurchaseOrderSyncLogsApi, poSyncLogEventTone } from "../PoSyncLogs/data";

const formatDateTime = (value) => {
	if (!value) return "—";
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const formatJson = (value) => {
	if (value == null || value === "") return null;
	try {
		const parsed = typeof value === "string" ? JSON.parse(value) : value;
		return JSON.stringify(parsed, null, 2);
	} catch {
		return String(value);
	}
};

const DetailRow = ({ label, children }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
			gap: { xs: 0.2, sm: 1.25 },
			py: 0.65,
			alignItems: "start",
		}}>
		<Typography sx={{ fontSize: 11, fontWeight: 700, color: poColors.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
			{label}
		</Typography>
		<Box sx={{ fontSize: 13, color: poColors.ink, wordBreak: "break-word" }}>{children}</Box>
	</Box>
);

const EventChip = ({ value }) => {
	const key = value || "QUEUED";
	const tone = poSyncLogEventTone[key] || poSyncLogEventTone.QUEUED;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				px: 1,
				py: 0.3,
				borderRadius: "8px",
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
			}}>
			{key}
		</Box>
	);
};

const PoSyncDetailsModal = ({ open, onClose, purchaseOrderId, meta = {} }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [logs, setLogs] = useState([]);

	useEffect(() => {
		if (!open || !purchaseOrderId) return;
		let cancelled = false;
		(async () => {
			setLoading(true);
			setError("");
			try {
				const result = await listPurchaseOrderSyncLogsApi({
					limit: 20,
					offset: 0,
					filter: { purchase_order_id: purchaseOrderId },
				});
				if (!cancelled) setLogs(result.rows || []);
			} catch (err) {
				if (!cancelled) setError(parseErrorMessage(err));
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, purchaseOrderId]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
				<Stack spacing={0.2}>
					<Typography sx={{ fontSize: 18, fontWeight: 700 }}>Sync details</Typography>
					<Typography variant="body2" color="text.secondary">
						{meta.po_no || `PO id ${purchaseOrderId}`}
					</Typography>
				</Stack>
				<IconButton onClick={onClose} size="small" aria-label="Close">
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ pt: 1.5, pb: 2.5 }}>
				<Box
					sx={{
						mb: 2,
						p: 1.5,
						borderRadius: "10px",
						border: `1px solid ${poColors.line}`,
						bgcolor: poColors.panelBg,
					}}>
					<Typography sx={{ fontSize: 11, fontWeight: 700, color: poColors.muted, letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.75 }}>
						Current
					</Typography>
					<Stack spacing={0.25} divider={<Divider flexItem sx={{ borderColor: poColors.line }} />}>
						<DetailRow label="Status">
							<PoStatusBadge status={meta.status || "DRAFT"} />
						</DetailRow>
						<DetailRow label="Ginesys Sync">
							<PoGinesysSyncBadge value={meta.ginesys_sync || (meta.status === "PUBLISHED" ? "PENDING" : "—")} />
						</DetailRow>
						<DetailRow label="Ginesys PO Id">{meta.ginesys_po_id ?? "—"}</DetailRow>
						<DetailRow label="Scheme Doc No">{meta.ginesys_scheme_doc_no || "—"}</DetailRow>
						<DetailRow label="Last error">
							<Typography sx={{ fontSize: 13, color: meta.ginesys_last_error ? poColors.danger : poColors.ink, whiteSpace: "pre-wrap" }}>
								{meta.ginesys_last_error || "—"}
							</Typography>
						</DetailRow>
					</Stack>
				</Box>

				<Typography sx={{ fontSize: 11, fontWeight: 700, color: poColors.muted, letterSpacing: "0.06em", textTransform: "uppercase", mb: 1 }}>
					Recent sync logs
				</Typography>

				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
						<CircularProgress size={28} />
					</Box>
				)}
				{error && (
					<Alert severity="error" sx={{ mb: 1 }}>
						{error}
					</Alert>
				)}
				{!loading && !error && !logs.length && (
					<Typography sx={{ fontSize: 13, color: poColors.muted }}>No sync logs for this PO yet.</Typography>
				)}

				<Stack spacing={1.25}>
					{logs.map((log) => {
						const req = formatJson(log.request_payload);
						const res = formatJson(log.response_payload);
						return (
							<Box
								key={log.id}
								sx={{
									p: 1.35,
									borderRadius: "10px",
									border: `1px solid ${poColors.line}`,
									bgcolor: poColors.white,
								}}>
								<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
									<EventChip value={log.event_type} />
									<PoGinesysSyncBadge value={log.ginesys_sync} />
									<Typography sx={{ fontSize: 12, color: poColors.muted, ml: "auto !important" }}>
										#{log.id} · {formatDateTime(log.created_at)}
									</Typography>
								</Stack>
								{log.error_message && (
									<Typography sx={{ fontSize: 12, color: poColors.danger, mb: 0.75, whiteSpace: "pre-wrap" }}>
										{log.error_message}
									</Typography>
								)}
								<Typography sx={{ fontSize: 12, color: poColors.muted, mb: 0.35 }}>
									Triggered by {log.triggered_by || "—"}
									{log.outbox_id != null ? ` · outbox ${log.outbox_id}` : ""}
									{log.ginesys_po_id != null ? ` · ginesys ${log.ginesys_po_id}` : ""}
								</Typography>
								{(req || res) && (
									<Stack spacing={0.75} sx={{ mt: 0.75 }}>
										{req && (
											<Box
												component="pre"
												sx={{
													m: 0,
													p: 1,
													maxHeight: 160,
													overflow: "auto",
													borderRadius: "8px",
													bgcolor: poColors.panelBg,
													border: `1px solid ${poColors.line}`,
													fontSize: 11,
													fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
													whiteSpace: "pre-wrap",
												}}>
												{req}
											</Box>
										)}
										{res && (
											<Box
												component="pre"
												sx={{
													m: 0,
													p: 1,
													maxHeight: 120,
													overflow: "auto",
													borderRadius: "8px",
													bgcolor: poColors.panelBg,
													border: `1px solid ${poColors.line}`,
													fontSize: 11,
													fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
													whiteSpace: "pre-wrap",
												}}>
												{res}
											</Box>
										)}
									</Stack>
								)}
							</Box>
						);
					})}
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default PoSyncDetailsModal;
